/**
 * VB Server - Tags API
 *
 * CRUD operations for organization tags (message categorization).
 *
 * @example
 * import { listTags, createTag, updateTag, deleteTag } from '@/api/server/tags';
 *
 * const tags = await listTags(orgId);
 * const newTag = await createTag(orgId, { tag: 'Priority', color: '#FF0000' });
 */

import { serverGet, serverPost, serverPostRaw, serverPut, serverDelete } from './client';
import { GLOBAL_ORG_ID } from '../../utils/constants';

// =============================================================================
// Types
// =============================================================================

/**
 * Tag entity from vb-server
 */
export interface Tag {
  tag_id: string;
  org_id: string;
  tag: string;
  description?: string | null;
  color: string;
  status: number;
  is_global?: boolean;
  created: string;
  modified: string;
}

/**
 * Input for creating a new tag
 */
export interface CreateTagInput {
  tag: string;
  description?: string;
  color?: string;
}

/**
 * Input for updating a tag
 */
export interface UpdateTagInput {
  tag?: string;
  description?: string;
  color?: string;
}

/**
 * Result of restoring a tag — includes an optional server warning about
 * messages that referenced the tag before deletion not being re-tagged.
 */
export interface RestoreTagResult {
  tag: Tag;
  warning?: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all tags for an organization (includes global tags)
 *
 * @param orgId - Organization ID
 * @returns Array of tags
 */
export async function listTags(
  orgId: string,
  options?: { includeInactive?: boolean }
): Promise<Tag[]> {
  const params = options?.includeInactive ? '?include_inactive=true' : '';
  return serverGet<Tag[]>(`/orgs/${orgId}/tags${params}`);
}

/**
 * Get a single tag by ID
 *
 * @param orgId - Organization ID
 * @param tagId - Tag ID
 * @returns Tag or null if not found
 */
export async function getTag(orgId: string, tagId: string): Promise<Tag | null> {
  try {
    const tags = await listTags(orgId);
    return tags.find((t) => t.tag_id === tagId) || null;
  } catch {
    return null;
  }
}

/**
 * Create a new tag
 *
 * @param orgId - Organization ID
 * @param data - Tag data
 * @returns Created tag
 */
export async function createTag(orgId: string, data: CreateTagInput): Promise<Tag> {
  return serverPost<Tag>(`/orgs/${orgId}/tags`, data);
}

/**
 * Update an existing tag
 *
 * Note: Global tags (org_id = '1') cannot be edited
 *
 * @param orgId - Organization ID
 * @param tagId - Tag ID
 * @param data - Updated tag data
 * @returns Updated tag
 */
export async function updateTag(orgId: string, tagId: string, data: UpdateTagInput): Promise<Tag> {
  return serverPut<Tag>(`/orgs/${orgId}/tags/${tagId}`, data);
}

/**
 * Soft-delete a tag (set status = 0)
 *
 * @param orgId - Organization ID
 * @param tagId - Tag ID
 */
export async function deleteTag(orgId: string, tagId: string): Promise<void> {
  await serverDelete(`/orgs/${orgId}/tags/${tagId}`);
}

/**
 * Restore a soft-deleted tag (set status = 1).
 * Returns the restored tag and an optional warning that messages previously
 * tagged with this tag were not re-tagged (cascade-removal is irreversible).
 *
 * @param orgId - Organization ID
 * @param tagId - Tag ID
 */
export async function restoreTag(orgId: string, tagId: string): Promise<RestoreTagResult> {
  const res = await serverPostRaw(`/orgs/${orgId}/tags/${tagId}/restore`, {});
  const json = (await res.json()) as { success: boolean; data: Tag; warning?: string };
  return { tag: json.data, warning: json.warning };
}

/**
 * Check if a tag is a global tag
 *
 * @param tag - Tag to check
 * @returns True if global tag
 */
export function isGlobalTag(tag: Tag): boolean {
  return tag.org_id === GLOBAL_ORG_ID;
}

/**
 * Get only org-specific tags (excluding global)
 *
 * @param orgId - Organization ID
 * @returns Array of org-specific tags
 */
export async function listOrgTags(orgId: string): Promise<Tag[]> {
  const tags = await listTags(orgId);
  return tags.filter((t) => t.org_id === orgId);
}

/**
 * Get only global tags
 *
 * @param orgId - Organization ID (used to fetch via the org endpoint)
 * @returns Array of global tags
 */
export async function listGlobalTags(orgId: string): Promise<Tag[]> {
  const tags = await listTags(orgId);
  return tags.filter((t) => t.org_id === GLOBAL_ORG_ID);
}
